import React from 'react';
import { ChatCircle, CheckCircle, FileText, Warning, MagnifyingGlass, ListBullets } from '@phosphor-icons/react';

const sevColor = (s) => s === 'high' ? 'bg-red-50 text-red-700 border-red-200' : s === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200';

export const ConcernAnalysisResults = ({ analysis }) => {
  if (!analysis) {
    return (
      <div className="mt-2 p-3 border border-dashed border-[#E5E7EB] rounded-md text-center mb-3">
        <p className="text-sm text-[#6B7280] font-ibm">Click <span className="font-semibold text-[#09090B]">Run Deep Analysis</span> for a structured report, or just start the conversation below.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4" data-testid="concern-analysis-results">
      {analysis.concern_restated && (
        <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md">
          <p className="text-xs font-bold tracking-[0.12em] uppercase text-[#6B7280] font-ibm mb-1">AI restatement</p>
          <p className="text-sm font-ibm text-[#09090B]">{analysis.concern_restated}</p>
        </div>
      )}

      {analysis.direct_response && (
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-1 flex items-center gap-2">
            <ChatCircle size={16} className="text-[#002FA7]" /> Direct Response
          </h4>
          <p className="text-sm font-ibm text-[#09090B] leading-relaxed whitespace-pre-wrap">{analysis.direct_response}</p>
        </div>
      )}

      {(analysis.supporting_facts || []).length > 0 && (
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-1 flex items-center gap-2">
            <CheckCircle size={16} className="text-[#34C759]" /> Supporting Facts ({analysis.supporting_facts.length})
          </h4>
          <div className="space-y-1.5">
            {analysis.supporting_facts.map((f, i) => (
              <div key={`fact-${i}`} className="p-2.5 border border-[#E5E7EB] rounded-md bg-white" data-testid={`concern-fact-${i}`}>
                <p className="text-sm font-ibm text-[#09090B]">{f.fact}</p>
                <p className="text-[11px] text-[#6B7280] font-ibm mt-0.5">
                  Source: <span className="font-semibold">{f.source || '—'}</span>{f.date ? ` · ${f.date}` : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(analysis.applicable_laws_cited || []).length > 0 && (
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-1 flex items-center gap-2">
            <FileText size={16} className="text-[#002FA7]" /> Applicable Laws Cited ({analysis.applicable_laws_cited.length})
          </h4>
          <div className="space-y-1.5">
            {analysis.applicable_laws_cited.map((l, i) => (
              <div key={`law-cited-${i}`} className="p-2.5 border border-[#E5E7EB] rounded-md bg-white">
                <p className="text-sm font-ibm font-semibold text-[#09090B]">{l.law_title}</p>
                <p className="text-xs text-[#6B7280] font-ibm mt-0.5">{l.how_it_applies}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(analysis.other_concerns_identified || []).length > 0 && (
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-1 flex items-center gap-2">
            <Warning size={16} className="text-[#FF9500]" /> Other Concerns Identified ({analysis.other_concerns_identified.length})
          </h4>
          <div className="space-y-1.5">
            {analysis.other_concerns_identified.map((c, i) => (
              <div key={`other-concern-${i}`} className="p-2.5 border border-[#E5E7EB] rounded-md bg-white flex items-start gap-2" data-testid={`concern-other-${i}`}>
                <span className={`text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border shrink-0 mt-0.5 ${sevColor(c.severity)}`}>{c.severity}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-ibm text-[#09090B]">{c.concern}</p>
                  <p className="text-[11px] text-[#6B7280] font-ibm mt-0.5">Evidence: {c.evidence || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(analysis.evidence_gaps || []).length > 0 && (
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-1 flex items-center gap-2">
            <MagnifyingGlass size={16} className="text-[#6B7280]" /> Evidence Gaps ({analysis.evidence_gaps.length})
          </h4>
          <ul className="list-disc pl-5 space-y-1">
            {analysis.evidence_gaps.map((g, i) => (
              <li key={`gap-${i}`} className="text-sm font-ibm text-[#09090B]">{g}</li>
            ))}
          </ul>
        </div>
      )}

      {(analysis.recommended_next_steps || []).length > 0 && (
        <div>
          <h4 className="font-outfit text-base font-semibold text-[#09090B] mb-1 flex items-center gap-2">
            <ListBullets size={16} className="text-[#002FA7]" /> Recommended Next Steps
          </h4>
          <div className="space-y-1.5">
            {analysis.recommended_next_steps.map((s, i) => (
              <div key={`next-step-${i}`} className="p-2.5 border border-[#E5E7EB] rounded-md bg-white flex items-start gap-2">
                <span className={`text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border shrink-0 mt-0.5 ${sevColor(s.urgency)}`}>{s.urgency}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-ibm font-semibold text-[#09090B]">{s.step}</p>
                  <p className="text-[11px] text-[#6B7280] font-ibm mt-0.5">{s.rationale}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.disclaimer && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-[11px] text-amber-900 font-ibm flex items-start gap-1.5">
            <Warning size={12} className="mt-0.5 shrink-0" /> {analysis.disclaimer}
          </p>
        </div>
      )}
    </div>
  );
};
